import { Div, Label, useWindowSize, Loader, Select, Icon, Touchable } from "@grassrootly/rn-core";
import { IPlayerGroup, GetGroupTopGoals, GetGroupTopAssists, GetGroupTopEXP, ITopGoals, ITopAssists, ITopEXP } from "@grassrootly/api";
import { MainStackScreenProps } from "../../../../navigation/Navigation.types";
import { useEffect, useState, useMemo } from "react";
import useBaseRequest from "../../../../api/BaseRequest";
import { Image, ScrollView, Platform } from "react-native";
import { images } from "../../../../layout/assets";
import moment from "moment";

type TabType = "goals" | "assists" | "exp";

interface GroupTopPlaysProps {
  group: IPlayerGroup;
  navigation: MainStackScreenProps<"group">["navigation"];
}

export default function GroupTopPlays({ group, navigation }: GroupTopPlaysProps) {
  const { isMobile } = useWindowSize();
  const [activeTab, setActiveTab] = useState<TabType>("goals");
  const [topGoals, setTopGoals] = useState<ITopGoals[]>([]);
  const [topAssists, setTopAssists] = useState<ITopAssists[]>([]);
  const [topEXP, setTopEXP] = useState<ITopEXP[]>([]);

  const selectOptions = useMemo(
    () => [
      { value: "goals", label: "Most goals in one match" },
      { value: "assists", label: "Most assists in one match" },
      { value: "exp", label: "Most EXP earned in one match" },
    ],
    []
  );

  const { execute: fetchTopGoals, loading: loadingGoals } = useBaseRequest(
    async (params: { groupId: number }) => {
      return GetGroupTopGoals(params);
    },
    {
      onCompleted: (data: ITopGoals[] | null) => {
        setTopGoals(data || []);
      }
    }
  );

  const { execute: fetchTopAssists, loading: loadingAssists } = useBaseRequest(
    async (params: { groupId: number }) => {
      return GetGroupTopAssists(params);
    },
    {
      onCompleted: (data: ITopAssists[] | null) => {
        setTopAssists(data || []);
      }
    }
  );

  const { execute: fetchTopEXP, loading: loadingEXP } = useBaseRequest(
    async (params: { groupId: number }) => {
      return GetGroupTopEXP(params);
    },
    {
      onCompleted: (data: ITopEXP[] | null) => {
        setTopEXP(data || []);
      }
    }
  );

  useEffect(() => {
    if (group?.id) {
      fetchTopGoals({ groupId: group.id });
      fetchTopAssists({ groupId: group.id });
      fetchTopEXP({ groupId: group.id });
    }
  }, [group?.id]);

  const loading = loadingGoals || loadingAssists || loadingEXP;

  const currentData = activeTab === "goals" ? topGoals : activeTab === "assists" ? topAssists : topEXP;
  const emptyMessage = activeTab === "goals" 
    ? `No goals scored yet in ${group.name}. Be the first to score!`
    : activeTab === "assists"
    ? `No assists made yet in ${group.name}. Be the first to assist!`
    : `No EXP earned yet in ${group.name}. Be the first to earn EXP!`;

  if (loading) {
    return (
      <ScrollView 
        showsVerticalScrollIndicator={false} 
        style={{ marginTop: -16 }}
        contentContainerStyle={{
          paddingTop: 24,
          paddingBottom: Platform.OS !== "web" ? 100 : 20,
          alignItems: "center",
          justifyContent: "center",
          minHeight: 300
        }}
      >
        <Loader size={32} />
      </ScrollView>
    );
  }

  return (
    <ScrollView 
      showsVerticalScrollIndicator={false} 
      style={{ marginTop: -16 }}
      contentContainerStyle={{
        paddingTop: 24,
        paddingBottom: Platform.OS !== "web" ? 100 : 20,
        paddingHorizontal: 16
      }}
    >
      {/* Select Control */}
      <Div classNames={["flexRow", "w100", "justifyContentCenter", "mb-{24}", "alignItemsCenter"]}>
        <Select
          options={selectOptions as any}
          selectedOption={activeTab}
          onSelect={(item: any) => setActiveTab(item.value)}
          placeholderText="Select category"
          forceSelectedOption
        />
      </Div>

      {/* Content Section */}
      <Div classNames={["mb-{32}"]}>
        {currentData.length > 0 ? (
          <Div classNames={["gap16"]}>
            {currentData.map((item, index) => {
              const count = activeTab === "goals" 
                ? (item as ITopGoals).player_goals_scored 
                : activeTab === "assists"
                ? (item as ITopAssists).player_assists_made
                : (item as ITopEXP).player_points_earned;

              return (
                <Touchable
                  key={`${item.player_id}-${item.game_id}`}
                  classNames={[
                    "flexRow",
                    "alignItemsCenter",
                    !isMobile ? "p16" : "p8",
                    index % 2 !== 0 ? "bgSecondaryMain" : "",
                    "gap16"
                  ]}
                  onPress={() => navigation?.navigate("game", { id: item.game_id })}
                >
                  {/* Rank */}
                  <Div classNames={["alignItemsCenter", "justifyContentCenter", "w-{32}", "h-{32}", "bgPrimaryMain", "rounded"]}>
                    <Label type="l3Bold" classNames={["colorWhite"]}>
                      {index + 1}
                    </Label>
                  </Div>

                  {/* Player Profile Picture */}
                  <Div classNames={["bgPrimaryWashed", "p8", "rounded"]}>
                    <Image
                      style={{ height: !isMobile ? 40 : 30, width: !isMobile ? 40 : 30, borderRadius: 40 }}
                      resizeMode="cover"
                      source={item.player_profile_picture ? { uri: item.player_profile_picture } : images.playerAvatar}
                    />
                  </Div>

                  {/* Player Info */}
                  <Div classNames={["flex1"]}>
                    <Label type={!isMobile ? "l2Bold" : "l3Bold"}>
                      {item.player_name}
                    </Label>
                    <Label type={!isMobile ? "l3Regular" : "l4Regular"} classNames={["colorSecondary"]}>
                      {moment(item.game_datetime).format("DD.MM.YY")}
                    </Label>
                  </Div>

                  {/* Count */}
                  <Div classNames={["bgPrimaryWashed", "p8", "rounded", "flexRow", "alignItemsCenter", "gap4"]}>
                    <Label type={!isMobile ? "l2Regular" : "l3Regular"} classNames={["colorSecondaryContrast"]}>
                      {count}
                    </Label>
                    {activeTab === "goals" ? (
                      <Icon name="sports_soccer" size={18} />
                    ) : activeTab === "assists" ? (
                      <Image source={images.assist} style={{ height: 14, width: 24 }} />
                    ) : (
                      <Label type={!isMobile ? "l2Regular" : "l3Regular"} classNames={["colorSecondaryContrast"]}>
                        EXP
                      </Label>
                    )}
                  </Div>
                </Touchable>
              );
            })}
          </Div>
        ) : (
          <Div classNames={["alignItemsCenter", "justifyContentCenter", "mt-{20}"]}>
            <Label type={!isMobile ? "l2Regular" : "l3Regular"} classNames={["textCenter", "colorSecondary"]}>
              {emptyMessage}
            </Label>
          </Div>
        )}
      </Div>
    </ScrollView>
  );
}
